/**
 * Authentication guard component
 *
 * Wraps protected routes to ensure the user is authenticated
 * or has opted in as a guest. Redirects unauthenticated users
 * to /login, preserving the intended destination for post-login redirect.
 */

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { FrogMascot } from './FrogMascot';
import { useAuthStore } from '@/stores/authStore';

const TAG = '[RequireAuth]';

/**
 * Route guard that requires authentication or guest mode.
 *
 * Waits for Zustand rehydration and Cognito session validation to complete
 * before making an auth decision — prevents false-positive access on page load.
 *
 * If the user is neither authenticated nor a guest, redirects to
 * /login with the current location stored in state.from for
 * post-login redirect. Otherwise renders the child route via Outlet.
 */
export function RequireAuth() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isGuest = useAuthStore((state) => state.isGuest);
  const isHydrating = useAuthStore((state) => state.isHydrating);
  const location = useLocation();

  useEffect(() => {
    if (isHydrating) {
      console.info(`${TAG} Waiting for auth hydration...`);
    } else if (!isAuthenticated && !isGuest) {
      console.info(`${TAG} Not authenticated — redirecting to /login`);
    } else {
      console.info(`${TAG} Auth confirmed (authenticated=${isAuthenticated}, guest=${isGuest})`);
    }
  }, [isHydrating, isAuthenticated, isGuest]);

  if (isHydrating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <FrogMascot variant="loading" size="lg" />
      </div>
    );
  }

  if (!isAuthenticated && !isGuest) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
